import React, { useMemo, useEffect, useRef } from 'react';
import { useFocusIdx, useBlock } from '@bay-email/editor';
import { IconLink } from '@arco-design/web-react/icon';
import { TextField } from '../../../components/Form';
import { Space } from '@arco-design/web-react';
import { MergeTags } from './MergeTags';
import { useField } from 'react-final-form';
import { t, isListingBlockType } from '@bay-email/core';
import type { MergeTagContext } from '@extensions/utils/getMergeTagsForContext';
import BaySwitch from '@bay-ui/BaySwitch';

export function Link({
	context,
	isCropperAllowed,
	isNewDynamicField,
}: {
	context?: MergeTagContext;
	isCropperAllowed?: boolean;
	isNewDynamicField?: boolean;
}) {
	const { focusIdx } = useFocusIdx();
	const { focusBlock, change } = useBlock();
	const { input: hrefInput } = useField(`${focusIdx}.attributes.href`, {
		parse: v => v,
	});
	const { input: targetInput } = useField(`${focusIdx}.attributes.target`, {
		parse: v => v,
	});

	// Track which blocks have had their default target value set to avoid marking form as dirty
	const initializedBlocksRef = useRef<Set<string>>(new Set());

	// Default to '_blank' if not set (toggle ON by default)
	// Only set once per block to avoid marking form as dirty on every render
	useEffect(() => {
		const currentValue = focusBlock?.attributes?.target;
		const blockKey = focusIdx || '';

		// Only set default if:
		// 1. Target is explicitly undefined (not set yet)
		// 2. We have a valid focusIdx
		// 3. We haven't already initialized this block
		if (
			currentValue === undefined &&
			focusIdx &&
			!initializedBlocksRef.current.has(blockKey)
		) {
			// Mark this block as initialized
			initializedBlocksRef.current.add(blockKey);
			// Set default value in block attributes
			change(`${focusIdx}.attributes.target`, '_blank');
		}
	}, [focusBlock?.attributes?.target, focusIdx, change]);

	// Initialize href with default value if not set
	useEffect(() => {
		const currentHref = focusBlock?.attributes?.href;
		// Check if it's a listing block type and href is not set
		if (!focusIdx || !focusBlock) return;
		const blockType = focusBlock.type;
		// All three listing blocks default to the same Listing URL field; the Multiple /
		// By Criteria renderer maps it to the loop's {{url}} (monday 13122054004).
		const isListingBlock = isListingBlockType(blockType);
		if (isListingBlock && !currentHref) {
			change(`${focusIdx}.attributes.href`, '{{listing.url}}');
		}
	}, [focusBlock, focusIdx, change]);

	// Normalize legacy button placeholder href value
	useEffect(() => {
		if (!focusIdx || !focusBlock) return;

		const blockType = focusBlock.type;
		const currentHref = focusBlock?.attributes?.href;
		const isButtonLikeBlock = blockType === 'button';

		if (isButtonLikeBlock && currentHref === '#') {
			change(`${focusIdx}.attributes.href`, '');
		}
	}, [focusBlock, focusIdx, change]);

	return useMemo(() => {
		// Determine placeholder based on block type
		const blockType = focusBlock?.type;
		let placeholder = '';
		if (isListingBlockType(blockType)) {
			placeholder = '{{listing.url}}';
		}

		return (
			<>
				{isCropperAllowed || isNewDynamicField ? (
					<>
						<Space className='my-2 w-100 d-flex justify-content-between align-items-center'>
							<span className='fw-bold text-capitalize'>{t('Link address')}</span>
							<MergeTags
								value={hrefInput.value}
								onChange={hrefInput.onChange}
								context={context || 'link-href'}
								children={
									<span className='dynamicFieldDropdown text-secondary cursor-pointer d-flex gap-2'>
										<span>{`{ }`}</span>
										<span className='dynamic-field'>{t('Dynamic Fields')}</span>
									</span>
								}
								className={`h-100 rounded-end-1 rounded-0 border border-start-0  mergeTag`}
							/>
						</Space>
						<div className='d-flex w-100 mb-2'>
							<TextField
								placeholder={placeholder || ''}
								formItem={{
									className: 'm-0',
									labelCol: {
										style: {
											display: 'none',
										},
									},
								}}
								name={`${focusIdx}.attributes.href`}
							/>
						</div>
					</>
				) : (
					<TextField
						prefix={<IconLink />}
						placeholder={placeholder}
						label={
							<Space>
								<span
									style={{
										fontWeight: 600,
										textTransform: 'capitalize',
										color: '#000000',
									}}
								>
									{t('Link address')}&nbsp;&nbsp;&nbsp;
								</span>
								<MergeTags
									value={hrefInput.value}
									onChange={hrefInput.onChange}
									iconOnly={true}
									context={context || 'link-href'}
								/>
							</Space>
						}
						name={`${focusIdx}.attributes.href`}
					/>
				)}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12,
					}}
				>
					<BaySwitch
						className='mb-0'
						checked={targetInput.value === '_blank'}
						onChange={() =>
							targetInput.onChange(targetInput.value === '_blank' ? '_self' : '_blank')
						}
					/>
					<span style={{ fontWeight: 400, display: 'inline-flex', alignItems: 'center' }}>
						{t('Open in a new tab')}
					</span>
				</div>
			</>
		);
	}, [
		focusIdx,
		hrefInput,
		targetInput,
		focusBlock,
		context,
		isCropperAllowed,
		isNewDynamicField,
	]);
}
